import React, { useEffect } from "react";

import CreditCardIcon from "@mui/icons-material/CreditCard";
import GroupIcon from "@mui/icons-material/Group";
import InsightsIcon from "@mui/icons-material/Insights";
import TagIcon from "@mui/icons-material/LocalOffer";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import SettingsIcon from "@mui/icons-material/Settings";
import {
  Typography,
  Stack,
  Container,
  Breadcrumbs,
  Link,
  Tab,
  Tabs,
  useTheme,
  alpha,
} from "@mui/material";
import { useParams, Link as RouterLink, useMatch } from "react-router";

import { ORGANIZATIONS } from "src/platform/constants/routes";
import { eventBus } from "src/platform/events/EventBus";
import { useActiveOrganization } from "src/platform/hooks/useActiveOrganization";
import { useTabParam } from "src/platform/hooks/useTabParam";
import { DotGrid } from "src/shell/components/DotGrid";
import ErrorCard from "src/shell/components/ErrorCard";
import { getContainerStyles, getLinkStyles } from "src/shell/styles/themeUtils";

import { useOrganizationLoader } from "../../hooks/useOrganizationLoader";
import MembersTab from "../MembersTab";

import DangerZone from "./DangerZone";
import FinOpsTab from "./FinOpsTab";
import GeneralSettingsCard from "./GeneralSettingsCard";
import OrganizationDetailsSkeleton from "./OrganizationDetailsSkeleton";
import OrganizationHeader from "./OrganizationHeader";
import OrgBillingTab from "./OrgBillingTab";
import { OrgMetricsPanel } from "./OrgMetricsPanel";
import TagSchemaTab from "./TagSchemaTab";
import TeamsTab from "./TeamsTab";

type OrgTab =
  "overview" | "members" | "teams" | "billing" | "finops" | "tags" | "settings";

const OrganizationDetails = () => {
  const { orgAlias: alias } = useParams<{ orgAlias: string }>();
  const isEditMode = !!useMatch(ORGANIZATIONS.EDIT.path);
  const { tab, setTab } = useTabParam<OrgTab>("overview");
  const {
    data: organization,
    loading,
    error,
    loadOrganization,
  } = useOrganizationLoader(eventBus);
  const { setActiveOrg } = useActiveOrganization();
  const theme = useTheme();

  useEffect(() => {
    if (alias) {
      void loadOrganization(alias);
      setActiveOrg(alias);
    }
  }, [alias, loadOrganization, setActiveOrg]);

  if (loading)
    return (
      <Container sx={getContainerStyles(theme)}>
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          sx={{ mb: theme.spacing(2) }}
        >
          <Link
            color="inherit"
            component={RouterLink}
            to={ORGANIZATIONS.LIST.path}
            sx={getLinkStyles(theme)}
          >
            Organizations
          </Link>
          <Typography color="text.primary">Loading…</Typography>
        </Breadcrumbs>
        <OrganizationDetailsSkeleton />
      </Container>
    );
  if (error)
    return (
      <ErrorCard
        error={error}
        title="Unable to load organization"
        onRetry={alias ? () => void loadOrganization(alias) : undefined}
      />
    );
  if (!organization)
    return (
      <ErrorCard
        error="No organization selected. Choose an organization from the list."
        title="No organization selected"
      />
    );

  return (
    <Container sx={getContainerStyles(theme)}>
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" />}
        sx={{ mb: theme.spacing(2) }}
      >
        <Link
          color="inherit"
          component={RouterLink}
          to={ORGANIZATIONS.LIST.path}
          sx={getLinkStyles(theme)}
        >
          Organizations
        </Link>
        <Typography color="text.primary">
          {organization?.name || "Loading..."}
        </Typography>
        {isEditMode && <Typography color="text.primary">Edit</Typography>}
      </Breadcrumbs>

      <DotGrid>
        <Stack spacing={4}>
          <OrganizationHeader
            organization={organization}
            onEditOrganization={() => setTab("settings")}
            compact={tab !== "overview"}
          />

          <Tabs
            value={tab}
            onChange={(_e, newTab: OrgTab) => setTab(newTab)}
            sx={{
              bgcolor: alpha(theme.palette.primary.main, 0.02),
              borderRadius: 1,
              minHeight: 40,
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 500,
                minHeight: 40,
              },
              "& .MuiTabs-indicator": {
                height: 3,
                borderRadius: 1.5,
                backgroundColor: theme.palette.primary.main,
              },
            }}
          >
            <Tab label="Overview" value="overview" />
            <Tab
              label="Members"
              value="members"
              icon={<GroupIcon fontSize="small" />}
              iconPosition="start"
            />
            <Tab
              label="Teams"
              value="teams"
              icon={<GroupIcon fontSize="small" />}
              iconPosition="start"
            />
            <Tab
              label="Billing"
              value="billing"
              icon={<CreditCardIcon fontSize="small" />}
              iconPosition="start"
            />
            <Tab
              label="FinOps"
              value="finops"
              icon={<InsightsIcon fontSize="small" />}
              iconPosition="start"
            />
            <Tab
              label="Tag Schema"
              value="tags"
              icon={<TagIcon fontSize="small" />}
              iconPosition="start"
            />
            <Tab
              label="Settings"
              value="settings"
              icon={<SettingsIcon fontSize="small" />}
              iconPosition="start"
            />
          </Tabs>

          {tab === "overview" && (
            <OrgMetricsPanel
              orgAlias={organization.alias}
              onNavigateBilling={() => setTab("billing")}
            />
          )}

          {tab === "members" && <MembersTab orgAlias={organization.alias} />}

          {tab === "teams" && <TeamsTab orgAlias={organization.alias} />}

          {tab === "billing" && <OrgBillingTab orgAlias={organization.alias} />}

          {tab === "finops" && <FinOpsTab orgAlias={organization.alias} />}

          {tab === "tags" && <TagSchemaTab orgAlias={organization.alias} />}

          {tab === "settings" && (
            <Stack spacing={2}>
              <GeneralSettingsCard
                organization={organization}
                onSaved={() => void loadOrganization(organization.alias)}
              />
              <DangerZone resourceName={organization.alias} />
            </Stack>
          )}
        </Stack>
      </DotGrid>
    </Container>
  );
};

export default OrganizationDetails;
